import { PartnerInfo } from '../models/partnerInfo/PartnerInfo';

/** Raised when a JOKA shop API could not be reached or answered with a server error. */
export class PartnerServiceUnavailableError extends Error {}

/**
 * Reads dealer data straight from the JOKA shop APIs (JOKA-305).
 *
 * The calls go out from the browser, the same way the order already reaches `config2basket`: the shop
 * credentials live in the front-end config next to `VENDOR_KEY`, and no service of ours sits in between.
 * Routing them through our backend was tried first and abandoned — the shop blocks requests coming from
 * our data centre, while an end customer's browser reaches it directly.
 */
export class PartnerApiService {
  private readonly api1Url = process.env.REACT_APP_CL__JOKA_API1_URL;
  private readonly api1Token = process.env.REACT_APP_CL__JOKA_API1_TOKEN;
  private readonly api2Url = process.env.REACT_APP_CL__JOKA_API2_URL;
  private readonly api2Token = process.env.REACT_APP_CL__JOKA_API2_TOKEN;

  /** B2C: dealer master data by customer number (API #1, relationhub "Customer API"). */
  async fetchDealerByKundennummer(kundennummer: string): Promise<PartnerInfo | undefined> {
    const url = `${this.api1Url}/api/v1/customers?id=${encodeURIComponent(kundennummer)}&per_page=1`;
    const payload = await this.get(url, { Authorization: `Bearer ${this.api1Token}` });
    if (!payload) {
      return undefined;
    }
    const record = Array.isArray(payload.data) ? payload.data[0] : undefined;
    return record ? toPartnerInfo(record, kundennummer) : undefined;
  }

  /** B2B: contact of the dealer logged into the shop (API #2), keyed by the session token. */
  async fetchShopContact(contextToken: string): Promise<PartnerInfo | undefined> {
    // The token travels URL-encoded in the iframe src (`|` as %7C); the shop expects it decoded.
    const decodedToken = contextToken.includes('%') ? safeDecode(contextToken) : contextToken;
    const payload = await this.get(`${this.api2Url}/api/vendor/v2/context/contact`, {
      Authorization: `Bearer ${this.api2Token}`,
      'IntelliShop-Context-Token': decodedToken
    });
    if (!payload?.success || !payload.contact) {
      return undefined;
    }
    return shopContactToPartnerInfo(payload.contact);
  }

  /**
   * @returns the parsed body, or `undefined` when the customer number / session is unknown (4xx).
   * @throws PartnerServiceUnavailableError on network failure or a 5xx — the caller has to tell
   *         "this dealer is not set up" apart from "the shop is unreachable right now".
   */
  private async get(url: string, headers: Record<string, string>): Promise<any | undefined> {
    let response: Response;
    try {
      response = await fetch(url, { headers: { Accept: 'application/json', ...headers } });
    } catch (e) {
      throw new PartnerServiceUnavailableError(`Request to the JOKA shop failed: ${(e as Error).message}`);
    }
    if (response.status >= 400 && response.status < 500) {
      // 401/403 mean the token is wrong — a configuration fault, not an unknown dealer.
      if (response.status === 401 || response.status === 403) {
        throw new PartnerServiceUnavailableError(`JOKA shop rejected our credentials (${response.status})`);
      }
      return undefined;
    }
    if (!response.ok) {
      throw new PartnerServiceUnavailableError(`JOKA shop answered ${response.status} ${response.statusText}`);
    }
    return await response.json();
  }
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** API #1 record → `PartnerInfo`. Contact fields are nested in `contacts[]`; we use the first entry. */
function toPartnerInfo(record: Record<string, any>, kundennummer: string): PartnerInfo {
  const contact = (Array.isArray(record.contacts) && record.contacts[0]) || {};
  const ansprechpartner = [contact.title, contact.fname, contact.lname].filter(Boolean).join(' ').trim();
  return clean({
    kundennummer: record.customer_id || kundennummer,
    name: record.name,
    ansprechpartner,
    email: contact.email,
    telefon: contact.phone || contact.mobile,
    adresse: composeAddress(record.street, record.zip, record.city, record.country),
    website: record.web,
    logo: record.media?.logo
  });
}

/** API #2 contact → `PartnerInfo`. Flat shape, and it carries a logo URL but no website. */
function shopContactToPartnerInfo(contact: Record<string, any>): PartnerInfo {
  const ansprechpartner = [contact.salutation, contact.title, contact.first_name, contact.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return clean({
    kundennummer: contact.customer_number_extern,
    name: contact.company_name,
    ansprechpartner,
    email: contact.email,
    telefon: contact.phone,
    adresse: composeAddress(contact.street, contact.zip, contact.city, contact.country),
    logo: contact.logo_url
  });
}

function composeAddress(street?: string, zip?: string, city?: string, country?: string): string {
  const cityLine = [zip, city].filter(Boolean).join(' ');
  return [street, cityLine, country].filter(Boolean).join(', ');
}

/** Drop empty/whitespace-only fields so the consumer can treat missing == absent. */
function clean(info: PartnerInfo): PartnerInfo {
  const result: PartnerInfo = {};
  for (const [key, value] of Object.entries(info)) {
    if (typeof value === 'string' && value.trim() !== '') {
      result[key as keyof PartnerInfo] = value.trim();
    }
  }
  return result;
}
